import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import MyTeamEmptyState from "./components/MyTeamEmptyState";
import MyTeamGuestState from "./components/MyTeamGuestState";
import MyTeamList from "./components/MyTeamList";
import CreateTeamFlexModal from "./components/CreateTeamFlexModal";
import TeamCreatedSuccessModal from "./components/TeamCreatedSuccessModal";
import { createNewTeamByPlayer, fetchTeamsByTeamAdminId } from "./api/myTeamApi";
import type { IMyTeam } from "./types/myTeam";
import type { CreateTeamFlexFormData } from "./components/CreateTeamFlexModal";
import { useAppSelector } from "../../redux/hooks";

function MyTeam() {
  const [teams, setTeams] = useState<IMyTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [createdTeam, setCreatedTeam] = useState<{ name: string; id: string } | null>(null);
  const userProfile = useAppSelector((s) => s.selectedProfile.playerProfile);

  const loadTeams = useCallback(async () => {
    setLoading(true);
    try {
      const list = await fetchTeamsByTeamAdminId();
      setTeams(list);
    } catch (err) {
      console.error("Failed to load teams", err);
      toast.error("Failed to load your teams. Please try again.");
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  const handleCreateTeam = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: CreateTeamFlexFormData) => {
      try {
        const payload = {
          teamName: data.teamName,
          teamCode: data.teamCode,
          description: data.description,
          image: data.image,
        };
        const res = await createNewTeamByPlayer(payload);
        const teamId = res.team?.teamId ?? res.teamSnapshotId;
        const teamName = res.teamName ?? data.teamName;
        setCreateModalOpen(false);
        setCreatedTeam({ name: teamName, id: teamId ?? "" });
        setSuccessModalOpen(true);
        await loadTeams();
      } catch (err) {
        console.error("Failed to create team", err);
        toast.error("Failed to create team. Please try again.");
      }
    },
    [loadTeams],
  );

  const handleSuccessDone = useCallback(() => {
    setSuccessModalOpen(false);
    setCreatedTeam(null);
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="myteam-loading">
          <span>Loading teams...</span>
        </div>
      );
    }
    if (userProfile == null) {
      return <MyTeamGuestState />;
    }
    if (teams.length === 0) {
      return <MyTeamEmptyState onCreateTeam={handleCreateTeam} />;
    }
    return <MyTeamList teams={teams} onCreateTeam={handleCreateTeam} />;
  };

  return (
    <div className="my-team">
      <div className={`myteam-container ${teams.length > 0 ? "myteam-container--with-list" : ""}`}>{renderContent()}</div>

      <CreateTeamFlexModal open={createModalOpen} onOpenChange={setCreateModalOpen} onSubmit={handleCreateSubmit} />

      <TeamCreatedSuccessModal open={successModalOpen} onOpenChange={setSuccessModalOpen} teamName={createdTeam?.name ?? ""} teamId={createdTeam?.id ?? ""} onDone={handleSuccessDone} />
    </div>
  );
}

export default MyTeam;
